import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatDetail, openWhatsApp } from "@/lib/api";
import { getLeadAttribution } from "@/lib/attribution";
import { TIERS } from "@/data/content";
import { SectionEyebrow } from "@/components/Marquee";

export default function ConsultationForm() {
  const [form, setForm] = useState({ name: "", phone: "", car: "", budget: "", city: "", tier: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.phone.trim().length < 6) {
      toast.error("Name and phone are required.");
      return;
    }
    try {
      setLoading(true);
      await api.post("/leads/consultation", { ...form, ...getLeadAttribution() });
      toast.success("Booked. Opening WhatsApp with your details…");
      const msg = `Hi CaarSaarthi, I just booked a consultation.\n\nName: ${form.name}\nPhone: ${form.phone}\nCar: ${form.car || "—"}\nBudget: ${form.budget || "—"}\nCity: ${form.city || "—"}\nTier: ${form.tier || "—"}\n\n${form.message || ""}`;
      openWhatsApp({ source: "consultation_form", tier: form.tier, message: msg });
      setForm({ name: "", phone: "", car: "", budget: "", city: "", tier: "", message: "" });
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="consult" className="relative py-20 md:py-28 bg-void" data-testid="consultation-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <SectionEyebrow testId="consult-eyebrow">Book Your Saarthi</SectionEyebrow>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="mt-6 font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight"
          >
            One form. One quote. <span className="italic gradient-text">One better deal.</span>
          </motion.h2>
          <p className="mt-6 text-silver/70 max-w-md leading-relaxed">
            Upload your dealer quotation once. We analyse it, identify negotiation opportunities, and tell you exactly
            what we can take on. We call within 12 hours with a benchmark. If we can't beat your best quote, you pay nothing.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="lg:col-span-7 rounded-3xl border border-white/10 bg-surface p-8 md:p-10 space-y-5"
          data-testid="consultation-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <FormField label="Your Name" testId="consult-name">
              <Input value={form.name} onChange={set("name")} placeholder="Rohan Malhotra" className="bg-void border-white/10 h-12 text-white" data-testid="consult-name-input" />
            </FormField>
            <FormField label="Phone" testId="consult-phone">
              <Input value={form.phone} onChange={set("phone")} placeholder="+91 98XXX XXXXX" className="bg-void border-white/10 h-12 text-white" data-testid="consult-phone-input" />
            </FormField>
            <FormField label="Car of Interest" testId="consult-car">
              <Input value={form.car} onChange={set("car")} placeholder="e.g. Hyundai Verna SX(O)" className="bg-void border-white/10 h-12 text-white" data-testid="consult-car-input" />
            </FormField>
            <FormField label="Budget (₹)" testId="consult-budget">
              <Input value={form.budget} onChange={set("budget")} placeholder="e.g. 15–20 lakh" className="bg-void border-white/10 h-12 text-white" data-testid="consult-budget-input" />
            </FormField>
            <FormField label="City" testId="consult-city">
              <Input value={form.city} onChange={set("city")} placeholder="Bengaluru" className="bg-void border-white/10 h-12 text-white" data-testid="consult-city-input" />
            </FormField>
            <FormField label="Preferred Tier" testId="consult-tier">
              <Select value={form.tier} onValueChange={set("tier")}>
                <SelectTrigger className="bg-void border-white/10 h-12 text-white" data-testid="consult-tier-trigger">
                  <SelectValue placeholder="Pick a tier" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-silver">
                  {TIERS.map((t) => (
                    <SelectItem key={t.id} value={t.name} data-testid={`consult-tier-option-${t.id}`}>
                      {t.name} · {t.priceLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <FormField label="Anything else?" testId="consult-message">
            <Textarea value={form.message} onChange={set("message")} placeholder="Timelines, exchange car, financing…" className="bg-void border-white/10 text-white" data-testid="consult-message-input" />
          </FormField>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.01] transition-transform disabled:opacity-60"
            data-testid="consult-submit"
          >
            {loading ? "Booking…" : "Book & Continue on WhatsApp"}
          </button>
        </form>
      </div>
    </section>
  );
}

function FormField({ label, testId, children }) {
  return (
    <label className="block" data-testid={`${testId}-field`}>
      <span className="eyebrow gradient-text">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
